import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ExternalLink, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_escuela-reports/artifacts/vmevlka0_Screenshot%202026-03-11%20at%206.06.04%E2%80%AFPM.png";
const FALLBACK_POLICIES_URL =
  "https://wespeakspanish-cr.com/about-us/policies-conditions/";

export default function AcceptPolicies() {
  const { user, refresh, logout, policies } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const policiesUrl = policies?.url || FALLBACK_POLICIES_URL;
  const isReAcceptance = !!user?.policies_accepted_at;
  const changeNote = policies?.change_note || "";

  const submit = async () => {
    if (!accepted) {
      toast.error(t("policies_must_accept"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users/me/accept-policies");
      await refresh();
      toast.success(t("policies_accepted_success"));
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-blue-50 px-4 py-8 flex items-center"
      data-testid="accept-policies-page"
    >
      <div className="max-w-2xl mx-auto w-full">
        <div className="flex items-center justify-between mb-6">
          <img src={LOGO_URL} alt="We Speak Spanish" className="h-10" />
          <button
            onClick={logout}
            className="text-xs text-slate-500 hover:text-red-600 underline-offset-2 hover:underline"
            data-testid="accept-policies-logout-btn"
          >
            {t("logout")}
          </button>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
          {/* Re-acceptance banner (shown only when user has previously accepted a different version) */}
          {isReAcceptance && (
            <div
              className="mb-5 p-4 rounded-lg border-2 border-brand-blue/40 bg-brand-blue/5 flex items-start gap-3"
              data-testid="policies-updated-banner"
            >
              <div className="p-1.5 rounded-md bg-brand-blue text-white shrink-0">
                <Sparkles className="h-4 w-4" />
              </div>
              <div className="text-sm">
                <p className="font-semibold text-slate-900">
                  {t("policies_updated_title")}
                </p>
                <p className="text-slate-700 mt-1">
                  {t("policies_updated_body")}
                </p>
                {changeNote && (
                  <p className="mt-2 text-xs text-slate-600 italic border-l-2 border-brand-blue pl-2">
                    <strong>{t("policies_whats_new")}:</strong> {changeNote}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 mb-4">
            <div className="p-2.5 rounded-lg bg-brand-blue/10 text-brand-blue">
              <FileText className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
                {t("policies_page_title")}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                {isReAcceptance
                  ? t("policies_page_subtitle_reaccept", { name: user?.name?.split(" ")[0] || "" })
                  : t("policies_page_subtitle", { name: user?.name?.split(" ")[0] || "" })}
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg bg-slate-50 border border-slate-200">
            <p className="text-sm text-slate-700 leading-relaxed">
              {isReAcceptance ? t("policies_page_body_reaccept") : t("policies_page_body")}
            </p>
            <a
              href={policiesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:text-blue-800 underline underline-offset-2"
              data-testid="policies-page-link"
            >
              {t("policies_read_here")}
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          <div
            className={`mt-5 p-4 rounded-lg border ${
              accepted
                ? "border-emerald-300 bg-emerald-50"
                : "border-amber-300 bg-amber-50"
            }`}
          >
            <Label className="text-sm flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={accepted}
                onCheckedChange={(v) => setAccepted(!!v)}
                data-testid="policies-page-accept-checkbox"
                className="mt-0.5"
              />
              <span className="leading-relaxed">
                {t("policies_accept_prefix")}{" "}
                <a
                  href={policiesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-blue font-semibold underline underline-offset-2 hover:text-blue-800"
                >
                  {t("policies_link_text")}
                </a>{" "}
                {t("policies_accept_suffix")}{" "}
                <span className="text-red-600">*</span>
              </span>
            </Label>
          </div>

          <div className="mt-8 flex justify-end">
            <Button
              onClick={submit}
              disabled={submitting || !accepted}
              data-testid="policies-page-submit-btn"
              className="btn-brand"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("saving")}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" /> {t("policies_accept_button")}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
